import React, { FC } from "react";
import { useTheme } from "@carrier-io/air-react/styles";
import Box from "@carrier-io/air-react/Box";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import InputAdornment from "@carrier-io/air-react/InputAdornment";
import { CreateUserHead } from "./CreateUserHead";
import MenuItem from "@carrier-io/air-react/MenuItem";
import Stack from "@carrier-io/air-react/Stack";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import { Skeleton, Tooltip } from "@carrier-io/air-react";
import Typography from "@carrier-io/air-react/Typography";
import { mockAuthMethod } from "../../mocks/createUserBasicInfo";
import { ControllableInput } from "../controllable/Input";
import { Control, FieldErrors } from "react-hook-form";
import { UserInput } from "../../validations/userCreateValidation";
import { ControllableSelect } from "../controllable/Select";

interface CreateUserProps {
  control: Control<UserInput>;
  errors?: Pick<
    FieldErrors<UserInput>,
    "email" | "firstName" | "lastName" | "authMethod"
  >;
  showAuthMethod?: boolean;
  emailDisabled?: boolean;
  isLoading?: boolean;
}

export const CreateUserBasicInfo: FC<CreateUserProps> = ({
  control,
  errors,
  showAuthMethod = true,
  emailDisabled = true,
  isLoading,
}) => {
  const theme = useTheme();
  const tooltipContent = mockAuthMethod
    .map((item) => `${item.name}: ${item.value}`)
    .join("\n");

  return (
    <Box
      sx={{
        paddingBottom: 3,
        marginBottom: 3,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <CreateUserHead
        title="Basic Information"
        description="User name and email address"
      />
      {isLoading ? (
        <Skeleton
          data-testid="basic-info-loading-skeleton"
          height={127}
          width={300}
        />
      ) : (
        <Box>
          <Stack direction="row" spacing={1} sx={{ marginBottom: 2 }}>
            <ControllableInput<UserInput>
              control={control}
              data-testid="firstName"
              name="firstName"
              label="First Name"
              error={errors?.firstName?.message}
              required
              showBorder
              hideBackgroundColor
              size="xlarge"
              sx={{ width: "100%", minWidth: "220px", maxWidth: "240px" }}
            />
            <ControllableInput<UserInput>
              control={control}
              name="lastName"
              label="Last Name"
              data-testid="lastName"
              error={errors?.lastName?.message}
              required
              showBorder
              hideBackgroundColor
              size="xlarge"
              sx={{ width: "100%", minWidth: "220px", maxWidth: "240px" }}
            />
          </Stack>
          <Stack sx={{ marginBottom: 2 }}>
            <ControllableInput<UserInput>
              control={control}
              name="email"
              hideBackgroundColor
              required
              data-testid="email"
              label="Email Address"
              error={errors?.email?.message}
              showBorder
              disabled={emailDisabled}
              size="xlarge"
              sx={{ maxWidth: 338, width: "100%" }}
              inputSetting={{
                endAdornment: (
                  <InputAdornment
                    disableTypography
                    position="start"
                    variant="standard"
                  >
                    <EmailOutlinedIcon fontSize="small" color="secondary" />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
          {showAuthMethod && (
            <Stack spacing={1} direction="row">
              <ControllableSelect
                id="authMethod"
                name="authMethod"
                label="Authentication Method"
                data-testid="authMethod"
                control={control}
                error={errors?.authMethod?.message}
                size="xlarge"
                inputLabelProps={{
                  required: true,
                }}
                formControlProps={{
                  sx: {
                    maxWidth: 338,
                    width: "100%",
                  },
                }}
                inputProps={{
                  sx: { minWidth: 150, width: "100%" },
                }}
              >
                {mockAuthMethod.map(({ name }) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </ControllableSelect>
              <Box sx={{ marginTop: "16px !important" }}>
                <Tooltip
                  enterDelay={100}
                  enterNextDelay={0}
                  enterTouchDelay={700}
                  leaveDelay={0}
                  leaveTouchDelay={1500}
                  placement="top"
                  title={
                    <Typography style={{ whiteSpace: "pre-line" }}>
                      {tooltipContent}
                    </Typography>
                  }
                >
                  <HelpOutlineOutlinedIcon fontSize="medium" />
                </Tooltip>
              </Box>
            </Stack>
          )}
        </Box>
      )}
    </Box>
  );
};
